import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Tab,
  Tabs,
  Select,
  MenuItem,
  Autocomplete,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Summarize as SummaryTypeIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ClinicDialog } from '../components/dialogs/ClinicDialog';
import ConfirmDeleteDialog from '../components/dialogs/ConfirmDeleteDialog';
import { CLINIC_DIALOG_SX } from '../constants/popupStyles';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import {
  summaryTypeService,
  SummaryType,
  SummaryTypeCreate,
  SummarySectionConfig,
  SummaryTypeKind,
  isTitrationKind,
} from '../services/summaryTypeService';
import { meetingTextService } from '../services/meetingTextService';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import RequirePermission from '../components/common/RequirePermission';
import { useCrudHandlers } from '../hooks/useCrudHandlers';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import PdfLayoutEditor from '../components/modals/PdfLayoutEditor';

const COLUMNS = [
  { key: 'name', label: 'Name', sortable: true, visible: true, defaultWidth: 300, minWidth: 150 },
  { key: 'sections', label: 'Sections', sortable: false, visible: true, defaultWidth: 200, minWidth: 100 },
];

interface SortableSectionRowProps {
  id: string;
  section: SummarySectionConfig;
  idx: number;
  sectionNameOptions: string[];
  onUpdate: (idx: number, patch: Partial<SummarySectionConfig>) => void;
  onDelete: (idx: number) => void;
}

const SortableSectionRow: React.FC<SortableSectionRowProps> = ({ id, section, idx, sectionNameOptions, onUpdate, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: 'relative',
    zIndex: isDragging ? 1 : 'auto',
  };

  return (
    <Box ref={setNodeRef} style={style} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
      <Box
        {...attributes}
        {...listeners}
        sx={{ cursor: 'grab', color: 'text.secondary', display: 'flex', alignItems: 'center', '&:active': { cursor: 'grabbing' } }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>

      <Select
        value={section.type}
        size="small"
        sx={{ minWidth: 130 }}
        onChange={(e) => {
          const newType = e.target.value as SummarySectionConfig['type'];
          const autoName = newType !== 'text' ? newType : section.name;
          onUpdate(idx, { type: newType, name: autoName });
        }}
      >
        <MenuItem value="text">Text</MenuItem>
        <MenuItem value="diagnostic">Diagnostic</MenuItem>
        <MenuItem value="medication">Medication</MenuItem>
      </Select>

      {section.type === 'text' ? (
        <Autocomplete
          freeSolo
          options={sectionNameOptions}
          value={section.name}
          onInputChange={(_, value) => onUpdate(idx, { name: value })}
          onChange={(_, value) => onUpdate(idx, { name: value || '' })}
          size="small"
          sx={{ flex: 1 }}
          renderInput={(params) => (
            <TextField {...params} placeholder="name (key)" size="small" />
          )}
        />
      ) : (
        <Box sx={{ flex: 1, px: 1 }}>
          <Typography variant="caption" color="text.secondary">
            name: <strong>{section.type}</strong>
          </Typography>
        </Box>
      )}

      <TextField
        value={section.label}
        size="small"
        placeholder="label (display)"
        sx={{ flex: 1.2 }}
        onChange={(e) => onUpdate(idx, { label: e.target.value })}
      />

      <Button size="small" color="error" onClick={() => onDelete(idx)}>✕</Button>
    </Box>
  );
};

const SummaryTypesPage: React.FC = () => {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<SummaryType | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<SummaryType | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [summaryKind, setSummaryKind] = useState<SummaryTypeKind>('psychiatric');
  const [summarySections, setSummarySections] = useState<SummarySectionConfig[]>([]);
  const [pdfLayout, setPdfLayout] = useState<any[]>([]);
  const [sectionNameOptions, setSectionNameOptions] = useState<string[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSummarySections((prev) => arrayMove(prev, Number(active.id), Number(over.id)));
    }
  };

  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm<SummaryTypeCreate>();

  const getSortValue = useCallback((item: SummaryType, key: string): any => {
    if (key === 'name') return item.name.toLowerCase();
    return '';
  }, []);

  const getSearchText = useCallback((item: SummaryType): string[] => [item.name], []);

  const table = useTablePage<SummaryType>({
    entityType: 'summaryTypes',
    columns: COLUMNS,
    tenantId: currentTenant?.id,
    getSortValue,
    getSearchText,
  });

  const loadData = useCallback(async () => {
    if (!currentTenant) return;
    table.loadData(() => summaryTypeService.getSummaryTypes(currentTenant.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant]);

  useEffect(() => { if (currentTenant) loadData(); }, [currentTenant, loadData]);

  useEffect(() => {
    meetingTextService.getSummarySectionNames().then(setSectionNameOptions).catch(() => {});
  }, []);

  const handleOpenDialog = (item?: SummaryType) => {
    if (item) {
      setEditingItem(item);
      setValue('name', item.name);
      setSummaryKind(item.type || 'psychiatric');
      setSummarySections(item.summary_sections || []);
      setPdfLayout(item.pdf_layout || []);
    } else {
      setEditingItem(null);
      reset();
      setSummaryKind('psychiatric');
      setSummarySections([]);
      setPdfLayout([]);
    }
    setActiveTab(0);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    reset();
    setSummarySections([]);
    setPdfLayout([]);
    setActiveTab(0);
  };

  const crud = useCrudHandlers<SummaryType>({
    table,
    createFn: (data) => summaryTypeService.createSummaryType({ ...data, tenant_id: currentTenant!.id, type: summaryKind, summary_sections: isTitrationKind(summaryKind) ? [] : summarySections, pdf_layout: pdfLayout }),
    updateFn: (id, data) => summaryTypeService.updateSummaryType(id as number, { ...data, type: summaryKind, summary_sections: isTitrationKind(summaryKind) ? [] : summarySections, pdf_layout: pdfLayout }),
    deleteFn: (id) => summaryTypeService.deleteSummaryType(id as number),
    entityName: 'Summary type',
    onSuccess: handleCloseDialog,
    onDeleteSuccess: () => { setDeleteDialogOpen(false); setItemToDelete(null); },
    reloadData: loadData,
  });

  const onSubmit = (data: SummaryTypeCreate) => {
    if (!currentTenant) { toast.error('No tenant selected'); return; }
    crud.handleCreateOrUpdate(data, editingItem);
  };

  const updateSection = (idx: number, patch: Partial<SummarySectionConfig>) => {
    setSummarySections((prev) => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
  };

  const getColumnValue = (item: SummaryType, key: string): React.ReactNode => {
    if (key === 'name') return <Typography variant="body2" fontWeight="medium">{item.name}</Typography>;
    if (key === 'sections') return (
      <Typography variant="body2" color="text.secondary">
        {item.summary_sections?.length ? `${item.summary_sections.length} section(s)` : '—'}
      </Typography>
    );
    return '';
  };

  const getContextMenuOptions = (): ContextMenuOption[] => {
    if (!table.selectedItem) return [];
    return [
      { label: 'Edit', icon: <EditIcon sx={{ mr: 2 }} />, onClick: () => { handleOpenDialog(table.selectedItem!); table.handleMenuClose(); } },
      { label: 'Delete', icon: <DeleteIcon sx={{ mr: 2 }} />, onClick: () => { setItemToDelete(table.selectedItem!); setDeleteDialogOpen(true); table.handleMenuClose(); }, color: 'error' as const },
    ];
  };

  const renderMobileCard = (item: SummaryType) => (
    <>
      <Box display="flex" alignItems="center" mb={1}>
        <SummaryTypeIcon sx={{ mr: 1, color: 'text.secondary' }} />
        <Typography variant="h6" fontWeight="medium">{item.name}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {item.summary_sections?.length ? `${item.summary_sections.length} section(s)` : 'No sections'}
        {item.pdf_layout?.length ? ` · PDF layout` : ''}
      </Typography>
    </>
  );

  return (
    <RequirePermission check={!!user?.user_privilege?.config} featureName="summary types">
      <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading summary types:">
        <DataTableToolbar
          {...table.toolbarProps}
          title="Summary Types"
          searchPlaceholder="Search summary types..."
          actions={
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} sx={{ whiteSpace: 'nowrap' }}>
              Add Summary Type
            </Button>
          }
        />

        {isMobile ? (
          <MobileCardGrid
            data={table.sortedData}
            renderCard={renderMobileCard}
            getItemKey={(item) => item.id}
            onMenuClick={table.handleRowClick}
            emptyIcon={<SummaryTypeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
            emptyMessage="No summary types found"
          />
        ) : (
          <DataTableContainer {...table.containerProps}>
            <DataTableBody
              data={table.sortedData}
              {...table.tableBodyProps}
              getColumnValue={getColumnValue}
              getRowKey={(item) => item.id}
              onRowClick={table.handleRowClick}
            />
          </DataTableContainer>
        )}

        <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

        {/* Create/Edit Dialog */}
        <ClinicDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          title={editingItem ? 'Edit Summary Type' : 'Create Summary Type'}
          maxWidth="md"
          onSubmit={handleSubmit(onSubmit)}
          actions={
            <>
              <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
                {editingItem ? 'Update' : 'Create'}
              </Button>
              <Button onClick={handleCloseDialog} sx={CLINIC_DIALOG_SX.cancelButton}>Cancel</Button>
            </>
          }
        >
          <Box>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tab label="Details" />
              {!isTitrationKind(summaryKind) && <Tab label="Summary Sections" />}
              <Tab label="PDF Layout" />
            </Tabs>

            {/* Tab 0: Details */}
            {activeTab === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                  <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Name</Typography>
                  <TextField
                    {...register('name', { required: 'Name is required' })}
                    fullWidth size="small"
                    error={!!errors.name} helperText={errors.name?.message}
                    InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                  />
                </Box>
                <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                  <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Type</Typography>
                  <Select
                    value={summaryKind}
                    onChange={(e) => { setSummaryKind(e.target.value as SummaryTypeKind); setActiveTab(0); }}
                    fullWidth size="small"
                  >
                    <MenuItem value="psychiatric">Psychiatric</MenuItem>
                    <MenuItem value="titration">Titration</MenuItem>
                    <MenuItem value="titration_followup">Titration Follow-Up</MenuItem>
                  </Select>
                </Box>
              </Box>
            )}

            {/* Tab 1: Summary Sections (psychiatric only) */}
            {!isTitrationKind(summaryKind) && activeTab === 1 && (
              <Box>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
                  <SortableContext items={summarySections.map((_, i) => String(i))} strategy={verticalListSortingStrategy}>
                    {summarySections.map((section, idx) => (
                      <SortableSectionRow
                        key={idx}
                        id={String(idx)}
                        section={section}
                        idx={idx}
                        sectionNameOptions={sectionNameOptions}
                        onUpdate={updateSection}
                        onDelete={(i) => setSummarySections(summarySections.filter((_, k) => k !== i))}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
                <Button
                  size="small" variant="outlined"
                  onClick={() => setSummarySections([...summarySections, { type: 'text', name: '', label: '' }])}
                  sx={{ mt: 1 }}
                >
                  + Add Section
                </Button>
              </Box>
            )}

            {/* PDF Layout tab (index 2 for psychiatric, 1 for titration) */}
            {activeTab === (isTitrationKind(summaryKind) ? 1 : 2) && (
              <PdfLayoutEditor
                layout={pdfLayout}
                onChange={setPdfLayout}
                summaryTypeType={summaryKind}
                tenantId={currentTenant?.id}
                sectionNames={summarySections.filter((s) => s.type === 'text' && s.name).map((s) => s.name)}
              />
            )}
          </Box>
        </ClinicDialog>

        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={() => crud.handleDelete(itemToDelete)}
          entityType="Summary Type"
          entityName={itemToDelete?.name}
        />

        <ManageColumnsModal {...table.manageColumnsProps} />
        <SaveViewModal {...table.saveViewModalProps} />
      </TablePageLayout>
    </RequirePermission>
  );
};

export default SummaryTypesPage;
